import React, { useRef, useState } from "react";
import {
  Mail, Loader2, Send, CheckCircle2, Paperclip, X,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";


// Match the backend caps in /app/backend/routes/contact.py. Kept in sync
// manually — there's only one place each cap is enforced authoritatively
// (the server) but mirroring them here lets us reject invalid picks
// before the user wastes time clicking submit.
const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIMES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
];


export default function ContactPage() {
  const copy = usePageContent("contact");
  const successCopy = usePageContent("contact_success");
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [files, setFiles] = useState([]); // File[]
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (incoming) => {
    setError("");
    const accepted = [];
    const errors = [];
    for (const file of incoming) {
      if (files.length + accepted.length >= MAX_ATTACHMENTS) {
        errors.push(`Max ${MAX_ATTACHMENTS} photos per message.`);
        break;
      }
      if (!ALLOWED_MIMES.includes(file.type)) {
        errors.push(`"${file.name}" isn't a supported image type.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        errors.push(`"${file.name}" is over ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB.`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length) setFiles((existing) => [...existing, ...accepted]);
    if (errors.length) setError(errors.join(" "));
  };

  const onFileChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    // Reset the input so picking the same file again still fires onchange.
    e.target.value = "";
  };

  const removeFile = (idx) => {
    setFiles((existing) => existing.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      if (form.subject.trim()) fd.append("subject", form.subject.trim());
      fd.append("message", form.message.trim());
      for (const f of files) fd.append("attachments", f);
      await api.post("/contact", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      setFiles([]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]"
      data-testid="page-contact"
    >
      <Header />
      <main className="mx-auto max-w-3xl px-5 pt-4 pb-16 sm:px-8 sm:pt-10 sm:pb-20">
        {copy.hide_header ? null : (
          <>
            <div className="flex items-center gap-2 text-brand-accent" style={kickerStyle(copy)}>
              <Mail size={16} strokeWidth={2.5} />
              <span className="label-kicker">{copy.kicker}</span>
            </div>
            <h1
              className="brand-headline mt-3 whitespace-pre-line font-display text-4xl font-black tracking-tight sm:text-5xl"
              style={titleStyle(copy)}
            >
              {copy.title}
            </h1>
            {copy.intro ? (
              <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--brand-body)]">
                {copy.intro}
              </p>
            ) : null}
          </>
        )}

        {done ? (
          <div
            className="mt-10 flex items-start gap-3 rounded-md border border-[#6CABDD] bg-[#6CABDD]/10 p-6"
            data-testid="contact-success"
          >
            <CheckCircle2 className="mt-0.5 text-[#6CABDD]" size={22} />
            <div>
              <p className="whitespace-pre-line font-display text-lg font-bold text-[var(--brand-ink)]">
                {successCopy.title}
              </p>
              {successCopy.intro ? (
                <p className="mt-1 text-sm text-[var(--brand-body)]">
                  {successCopy.intro}
                </p>
              ) : null}
              <button
                onClick={() => setDone(false)}
                data-testid="contact-send-another"
                className="mt-3 label-kicker text-[var(--brand-ink)] underline underline-offset-4"
              >
                Send another →
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-10 space-y-5 rounded-md border border-black/10 bg-white p-6 sm:p-8"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Your name" required>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name"
                  className="mt-2 block w-full rounded-md border border-black/15 bg-white px-4 py-2.5 font-display text-[var(--brand-ink)] outline-none focus:border-[#0A0F17]"
                />
              </Field>
              <Field label="Your email" required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email"
                  className="mt-2 block w-full rounded-md border border-black/15 bg-white px-4 py-2.5 font-display text-[var(--brand-ink)] outline-none focus:border-[#0A0F17]"
                />
              </Field>
            </div>
            <Field label="Subject (optional)">
              <input
                type="text"
                maxLength={200}
                value={form.subject}
                onChange={update("subject")}
                data-testid="contact-subject"
                placeholder={copy.subject_placeholder || "What's this about?"}
                className="mt-2 block w-full rounded-md border border-black/15 bg-white px-4 py-2.5 font-display text-[var(--brand-ink)] outline-none focus:border-[#0A0F17]"
              />
            </Field>
            <Field label="Message" required>
              <textarea
                required
                rows={8}
                maxLength={5000}
                value={form.message}
                onChange={update("message")}
                data-testid="contact-message"
                placeholder={copy.message_placeholder || "Tip, feedback, takedown request — whatever it is, write away."}
                className="mt-2 block w-full rounded-md border border-black/15 bg-white px-4 py-3 font-display text-base leading-relaxed outline-none focus:border-[#0A0F17]"
              />
            </Field>

            {/* Photo attachments */}
            <div data-testid="contact-attachments">
              <span className="label-kicker text-[var(--brand-body)]">
                Photos (optional · up to {MAX_ATTACHMENTS} · {MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB each)
              </span>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {files.map((f, idx) => (
                  <span
                    key={`${f.name}-${idx}`}
                    data-testid="contact-attachment-pill"
                    className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-[#F8FAFC] py-1 pl-3 pr-1.5 text-sm"
                  >
                    <Paperclip size={12} strokeWidth={2.5} />
                    <span className="max-w-[180px] truncate" title={f.name}>
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      data-testid="contact-attachment-remove"
                      aria-label={`Remove ${f.name}`}
                      className="rounded-full p-1 text-[var(--brand-body)] hover:bg-black/5 hover:text-[#E63946]"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                {files.length < MAX_ATTACHMENTS ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="contact-attachment-add"
                    className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-black/20 bg-white px-3 py-1.5 font-display text-sm font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17]"
                  >
                    <Paperclip size={12} strokeWidth={2.5} /> Add photo
                  </button>
                ) : null}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_MIMES.join(",")}
                  onChange={onFileChange}
                  data-testid="contact-attachment-input"
                  className="hidden"
                />
              </div>
            </div>

            {error ? (
              <p
                className="rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]"
                data-testid="contact-error"
              >
                {error}
              </p>
            ) : null}

            <div className="flex items-center gap-3 border-t border-black/5 pt-5">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-5 py-3 font-display text-sm font-bold uppercase tracking-wide text-white hover:-translate-y-0.5 hover:bg-black disabled:opacity-60"
              >
                {submitting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                {submitting ? "Sending…" : "Send message"}
              </button>
              <span className="label-kicker text-[var(--brand-body)]">
                We never share your email.
              </span>
            </div>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="label-kicker text-[var(--brand-body)]">
      {label}
      {required ? <span className="ml-1 text-[#E63946]">·</span> : null}
    </span>
    {children}
  </label>
);
